import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Star, Package, GripVertical, Search, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ProductPicker } from './ProductPicker';
import { Product, ProductVariation } from '../types';

interface VariationsManagerProps {
  variations: ProductVariation[];
  onVariationsChange: (variations: ProductVariation[]) => void;
  currentProductId?: string;
  currentProductName?: string;
}

interface VariationGroup {
  label: string;
  variations: ProductVariation[];
}

export function VariationsManager({ 
  variations, 
  onVariationsChange, 
  currentProductId,
  currentProductName 
}: VariationsManagerProps) {
  const [showModal, setShowModal] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [editingVariation, setEditingVariation] = useState<{
    groupLabel: string;
    variationId?: string;
  } | null>(null);
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [newVariationValue, setNewVariationValue] = useState('');

  // Group variations by label
  const variationGroups: VariationGroup[] = variations.reduce((groups, variation) => {
    const existingGroup = groups.find(g => g.label === variation.label);
    if (existingGroup) {
      existingGroup.variations.push(variation);
    } else {
      groups.push({ label: variation.label, variations: [variation] });
    }
    return groups;
  }, [] as VariationGroup[]);

  // Sort variations within each group by sortOrder
  variationGroups.forEach(group => {
    group.variations.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  });

  const generateVariationId = () => {
    return `var_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const addNewGroup = () => {
    if (!newGroupLabel.trim()) return;
    
    setEditingVariation({
      groupLabel: newGroupLabel.trim(),
      variationId: undefined
    });
    setShowModal(true);
  };

  const addVariationToGroup = (groupLabel: string) => {
    setEditingVariation({
      groupLabel,
      variationId: undefined
    });
    setShowModal(true);
  };

  const handleProductSelect = (product: Product) => {
    if (!editingVariation || !newVariationValue.trim()) return;

    const newVariation: ProductVariation = {
      id: generateVariationId(),
      label: editingVariation.groupLabel,
      value: newVariationValue.trim(),
      linkedProductId: product.id,
      isDefault: false,
      sortOrder: variations.filter(v => v.label === editingVariation.groupLabel).length
    };

    const updatedVariations = [...variations, newVariation];
    onVariationsChange(updatedVariations);
    
    // Reset and close
    setShowModal(false);
    setShowProductPicker(false);
    setEditingVariation(null);
    setNewVariationValue('');
    setNewGroupLabel('');
  };

  const closeModal = () => {
    setShowModal(false);
    setShowProductPicker(false);
    setEditingVariation(null);
    setNewVariationValue('');
  };

  const closeProductPicker = () => {
    setShowProductPicker(false);
  };

  const removeVariation = (variationId: string) => {
    const updatedVariations = variations.filter(v => v.id !== variationId);
    onVariationsChange(updatedVariations);
  };

  const removeGroup = (groupLabel: string) => {
    const updatedVariations = variations.filter(v => v.label !== groupLabel);
    onVariationsChange(updatedVariations);
  };

  const toggleDefault = (variationId: string) => {
    const updatedVariations = variations.map(v => ({
      ...v,
      isDefault: v.id === variationId ? !v.isDefault : v.isDefault
    }));
    onVariationsChange(updatedVariations);
  };

  const updateVariationValue = (variationId: string, newValue: string) => {
    const updatedVariations = variations.map(v => 
      v.id === variationId ? { ...v, value: newValue } : v
    );
    onVariationsChange(updatedVariations);
  };


  return (
    <Card className="shadow-sm border-gray-200">
      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-purple-600" />
            Product Variations
          </div>
          <Badge 
            variant="outline" 
            className={`px-3 py-1 text-sm font-medium transition-all duration-300 ${
              variations.length > 0 
                ? 'bg-purple-50 text-purple-700 border-purple-300'
                : 'bg-gray-50 text-gray-500 border-gray-300'
            }`}
          >
            {variations.length} variation{variations.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Current Product Indicator */}
        {currentProductName && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center">
                <Star className="h-4 w-4 text-white fill-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-purple-900">Current Product</p>
                <p className="text-sm text-purple-700">{currentProductName}</p>
              </div>
            </div>
          </div>
        )}

        {/* Variation Groups */}
        <AnimatePresence>
          {variationGroups.map((group) => (
            <motion.div
              key={group.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-3"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-gray-400" />
                  {group.label}
                </h4>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addVariationToGroup(group.label)}
                    className="h-8 px-3 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeGroup(group.label)}
                    className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {group.variations.map((variation) => (
                  <motion.div
                    key={variation.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200
                      ${variation.isDefault 
                        ? 'border-purple-300 bg-purple-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`
                        flex items-center justify-center w-6 h-6 rounded-full border-2 transition-all duration-200
                        ${variation.isDefault 
                          ? 'border-purple-500 bg-purple-500' 
                          : 'border-gray-300 bg-white'
                        }
                      `}>
                        {variation.isDefault && <Star className="w-3 h-3 text-white fill-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Input
                          value={variation.value}
                          onChange={(e) => updateVariationValue(variation.id, e.target.value)}
                          className="h-8 text-sm border-none bg-transparent p-0 focus:ring-0 focus:border-none"
                          placeholder="Variation value"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Product ID: {variation.linkedProductId}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleDefault(variation.id)}
                        className="h-8 w-8 p-0 text-purple-600 hover:bg-purple-50"
                        title={variation.isDefault ? "Remove default" : "Set as default"}
                      >
                        <Star className={`h-3 w-3 ${variation.isDefault ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVariation(variation.id)}
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Add New Group */}
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Input
              value={newGroupLabel}
              onChange={(e) => setNewGroupLabel(e.target.value)}
              placeholder="Enter variation label (e.g., Size, Color, Flavor)"
              className="h-10 max-w-xs rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20"
              onKeyPress={(e) => e.key === 'Enter' && addNewGroup()}
            />
            <Button
              type="button"
              onClick={addNewGroup}
              disabled={!newGroupLabel.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 h-10 rounded-xl"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Group
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Create variation groups like "Size", "Colour", or "Flavour" to organize product options
          </p>
        </div>

        {/* Info Box */}
        {variations.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">About Product Variations</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Create variations to link related products together. For example, link different sizes 
                  or colors of the same product. Customers will see these as selectable options on the product page.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Variation Value Modal */}
      {showModal && editingVariation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10003] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Variation</h3>
              <p className="text-sm text-gray-600 mt-1">
                Adding to: <span className="font-medium">{editingVariation.groupLabel}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Variation Value
                </label>
                <Input
                  value={newVariationValue}
                  onChange={(e) => setNewVariationValue(e.target.value)}
                  placeholder="e.g., Large, Red, Vanilla"
                  className="h-10 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be displayed as the variation option
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowProductPicker(true)}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={!newVariationValue.trim()}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Select Product
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProductPicker
        isOpen={showProductPicker}
        onClose={closeProductPicker}
        onSelect={handleProductSelect}
        excludeProductIds={[]}
        currentProductId={currentProductId}
      />
    </Card>
  );
} 